import { z } from 'zod';
import { FLEET_SUMMARY_SILENT_AFTER_HOURS } from './fleet-summary.js';
import { MAX_QUERY_DIMENSIONS, uniqueVehiclePin, VEHICLE_PINNING_DIMENSIONS, queryDocumentSchema, validateQuery, } from './query-document.js';
import { CATALOGUE_GRAIN_TIME, catalogueEntry } from './query-catalogue.js';
import { queryRoutingSchema } from './query-provenance.js';
/**
 * The AI Operations Copilot's capability registry.
 *
 * `docs/architecture/ai-copilot-design.md` §6 — **D4: no text-to-SQL, ever.**
 * Tenant isolation in OIP is application-enforced, guarded by 58 architecture
 * rules, and row-level security is deliberately not active. A model emitting SQL
 * would route around every one of those guards in a single feature.
 *
 * So a question resolves to a CAPABILITY: a declared key, a typed parameter
 * schema, the roles allowed to run it, the entitlement it needs, and a runner
 * that issues ordinary repository calls. The model — when it eventually exists —
 * may choose between capabilities and reword the answer. It may not reach the
 * data.
 *
 * This file holds the DECLARATION only. The runners live in `apps/api`, because
 * they need repositories and this package must not. A parity test asserts every
 * declared capability has a runner and vice versa, the same way
 * `POLICY_EVENT_TYPES` is kept in step with `EVENT_TYPES`.
 */
// ── vocabulary ──────────────────────────────────────────────────────────────
/**
 * The first fact of the provenance strip (D2).
 *
 * There is no confidence percentage, deliberately: §0.2 — a fabricated number in
 * the most authoritative position on the screen is the worst available option.
 * This says where the words came from instead, and it is checkable.
 */
export const COPILOT_DETERMINISM = ['COMPUTED', 'REWORDED', 'PROJECTED'];
export const copilotDeterminismSchema = z.enum(COPILOT_DETERMINISM);
/** Modes are a VIEW over this registry, never a second taxonomy (D11). */
export const COPILOT_MODES = ['INVESTIGATE', 'ANALYZE', 'RECOMMEND', 'REPORT', 'AUTOMATE'];
export const copilotModeSchema = z.enum(COPILOT_MODES);
export const COPILOT_MODE_LABELS = {
    INVESTIGATE: 'Investigate',
    ANALYZE: 'Analyze',
    RECOMMEND: 'Recommend',
    REPORT: 'Report',
    AUTOMATE: 'Automate',
};
/** How a finding's `data` is laid out. The renderer never sees free text where a number belongs. */
export const COPILOT_RENDERERS = ['TABLE', 'KPI', 'TREND', 'CARDS', 'TIMELINE', 'MAP'];
export const copilotRendererSchema = z.enum(COPILOT_RENDERERS);
/**
 * What an evidence row points at (§7.3).
 *
 * Each kind knows where it opens: an `INCIDENT` opens the incident, a `POLICY`
 * opens that policy version rendered in English, a `RISK_FACTOR` opens the
 * assessment showing its `scoreImpact` and `reason`.
 */
export const COPILOT_EVIDENCE_KINDS = [
    'VEHICLE',
    'INCIDENT',
    'EVENT',
    'POLICY',
    'RISK_FACTOR',
    'RECOMMENDATION',
    'TELEMETRY',
    /**
     * A file a human attached — a photo, an invoice, an inspection report.
     *
     * The row carries the filename, its kind and who uploaded it. It does NOT
     * carry the bytes and never will from here: downloading an attachment is
     * already an audited, presigned, short-TTL operation on the recommendation
     * that owns it, and a second path to the same bytes would be a second thing
     * to secure and a hole in that audit.
     */
    'ATTACHMENT',
    /**
     * A video clip for an event — declared now, produced by nothing.
     *
     * OIP has no video ingestion: no pipeline, no storage of footage, no
     * event-to-clip mapping. The kind exists so the ledger, the renderer and the
     * evidence contract are ready for one integration rather than a rewrite, and
     * so the gap is visible in the vocabulary instead of being discovered later.
     */
    'EVENT_CLIP',
];
export const copilotEvidenceKindSchema = z.enum(COPILOT_EVIDENCE_KINDS);
// ── the registry ────────────────────────────────────────────────────────────
/**
 * How an answer behaves across branches — **D19**.
 *
 * - `MERGEABLE` — declares a per-field merge rule and answers organisation-wide.
 * - `CONCATENABLE` — a list, combined by appending with a branch column. Safe
 *   because appending invents nothing; only the ordering is a judgement.
 * - `SUBJECT_SCOPED` — the scope comes from the ENTITY asked about rather than
 *   from a branch selection. A vehicle belongs to exactly one branch, so the
 *   answer reports the branch its subject lives in and neither merges nor
 *   refuses.
 */
export const COPILOT_SCOPE_SHAPES = ['MERGEABLE', 'CONCATENABLE', 'SUBJECT_SCOPED'];
export const copilotScopeShapeSchema = z.enum(COPILOT_SCOPE_SHAPES);
/**
 * What a finding offers to do next.
 *
 * Every one is a deep link into an existing screen with fields prefilled — D12:
 * *the copilot prepares a write, it never commits one.* If it committed, the
 * audit would record a human doing what a model decided; preparing inherits
 * every guard and every audit row that screen already has, and adds no new write
 * path to secure.
 */
export const COPILOT_ACTIONS = [
    'OPEN_VEHICLE',
    'OPEN_INCIDENT',
    'OPEN_RECOMMENDATION',
    'OPEN_POLICY',
    'VIEW_INCIDENT_QUEUE',
    'VIEW_RECOMMENDATION_QUEUE',
    'VIEW_DASHBOARD',
    'VIEW_REPORTS',
    'COMPARE_PERIOD',
];
export const copilotActionSchema = z.enum(COPILOT_ACTIONS);
export const COPILOT_ACTION_LABELS = {
    OPEN_VEHICLE: 'Open vehicle',
    OPEN_INCIDENT: 'Open incident',
    OPEN_RECOMMENDATION: 'Open recommendation',
    OPEN_POLICY: 'Open policy',
    VIEW_INCIDENT_QUEUE: 'View incident queue',
    VIEW_RECOMMENDATION_QUEUE: 'View recommendation queue',
    VIEW_DASHBOARD: 'Open dashboard',
    VIEW_REPORTS: 'Open reports',
    COMPARE_PERIOD: 'Compare with previous period',
};
export const COPILOT_CAPABILITY_KEYS = [
    'fleet.query',
    'attention.today',
    'incidents.critical.today',
    'vehicle.risk.explain',
    'recommendation.explain',
    'ops.period.summary',
    'ops.period.compare',
    'driver.repeat.behaviour',
    'vehicle.health.watchlist',
    'fuel.loss.locate',
    'fuel.used.rank',
    'depot.idle.rank',
    'fleet.silent',
    'driver.improvement',
    'can.series',
    'evidence.attachments',
    'report.generate',
    'ops.overnight.brief',
];
export const copilotCapabilityKeySchema = z.enum(COPILOT_CAPABILITY_KEYS);
export const COPILOT_CAPABILITIES = {
    'vehicle.risk.explain': {
        question: 'Why does this vehicle carry the risk it does?',
        examples: [
            'Why is AV-1042 high risk?',
            'Explain the risk on this vehicle',
            'What is driving the score for AV-1042?',
        ],
        /*
         * v1 is these two roles (R10a). OPERATOR and VIEWER arrive later as a
         * product decision rather than a security review, which is why D16 ships
         * now even though the restriction makes it invisible.
         */
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'INVESTIGATE',
        render: 'CARDS',
        /*
         * False, and it is a constraint on the runner rather than a description of
         * it: incidents carry a `driverId`, so an explanation that mentioned who was
         * driving would identify a person and would have to inherit that person's
         * authorization. v1 explains the VEHICLE. A test asserts the result carries
         * no driver identity, so this declaration cannot quietly become untrue.
         */
        identifiesIndividuals: false,
        /*
         * SUBJECT_SCOPED, corrected from `orgWide: false` — D19.
         *
         * "Refused" was the wrong word and would have shown the wrong thing on the
         * finding. A vehicle belongs to exactly one branch, so there is nothing to
         * merge and nothing to refuse: the answer reports the branch its subject
         * lives in. Declaring a refusal here would have made the scope fact wrong
         * rather than absent, which is the failure the provenance strip exists to
         * prevent.
         */
        scopeShape: 'SUBJECT_SCOPED',
        actions: ['OPEN_VEHICLE', 'OPEN_INCIDENT', 'VIEW_RECOMMENDATION_QUEUE'],
    },
    /**
     * The declared query language, asked through the copilot.
     *
     * ⚠️ **Its PARAMETERS are a whole QueryDocument, and that is D4 rather than a
     * departure from it.** "There is no path by which a question reaches the
     * database except through a capability somebody wrote" — this is that
     * capability, and everything it can reach is a field somebody admitted to the
     * catalogue. A model may FILL the document; it may not author a query (G1).
     *
     * ⚠️ **CONCATENABLE, not MERGEABLE, and the difference is honesty.** A
     * capability's `mergeRules` are fixed at declaration time and a query's
     * measures vary per call, so there is no static rule that could cover them.
     * Appending each branch's rows under its own name invents nothing; summing
     * them would need the catalogue's per-measure merge rules applied at fan-out,
     * which is real work and is not built. Stated rather than left to be
     * discovered: at organisation level this answers PER BRANCH.
     *
     * ⚠️ **`identifiesIndividuals` is TRUE even though no answer names a person.**
     * There is no driver-name dimension in the catalogue, so a row cannot carry
     * one — but the EVIDENCE drill-through projects a driver's name, and a
     * declaration that described only the answer would be false about the thing a
     * reader actually opens.
     */
    'fleet.query': {
        question: 'Answer a declared query about this fleet',
        examples: [
            'How many incidents by type this month?',
            'Vehicles by class and status',
            'Total distance by how it was measured',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        /*
         * No extra gate here: every copilot route already carries `AI_COPILOT`, and
         * the fields inside the document carry their own entitlements — a reading
         * needs `TELEMETRY` whichever surface asks for it. A capability-level
         * feature would be a third gate saying less than the two that exist.
         */
        feature: null,
        mode: 'ANALYZE',
        render: 'TABLE',
        identifiesIndividuals: true,
        scopeShape: 'CONCATENABLE',
        actions: [],
    },
    'attention.today': {
        question: 'Which vehicles need attention today?',
        examples: [
            'What needs attention today?',
            'Which vehicles should I look at first?',
            'Show me the vehicles with open work',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        /*
         * A list, so branches combine by APPENDING with a branch column. Safe
         * because appending invents nothing — unlike a merged average, the only
         * judgement is the ordering, and that is re-applied over the union.
         */
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE', 'VIEW_RECOMMENDATION_QUEUE'],
    },
    'incidents.critical.today': {
        question: 'Which critical incidents were raised recently?',
        examples: [
            'What critical incidents came in today?',
            'Show me the critical incidents this week',
            'Anything critical overnight?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE'],
    },
    'recommendation.explain': {
        question: 'Why was this recommendation made?',
        examples: [
            'Why is this being recommended?',
            'Explain this recommendation',
            'What rule produced this?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'RECOMMEND',
        render: 'CARDS',
        identifiesIndividuals: false,
        scopeShape: 'SUBJECT_SCOPED',
        actions: ['OPEN_RECOMMENDATION', 'OPEN_INCIDENT', 'OPEN_POLICY'],
    },
    'ops.period.summary': {
        question: 'How did the fleet perform over this period?',
        examples: [
            'How did we do last week?',
            'Summarise the last 30 days',
            'Give me the operations summary',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'ANALYZE',
        render: 'KPI',
        identifiesIndividuals: false,
        /*
         * MERGEABLE, and the rules are not invented here: they are the ones
         * `FLEET_SUMMARY_MERGE_RULES` already declares and a test already pins to
         * the response schema. Restating them would create a second copy to drift.
         */
        scopeShape: 'MERGEABLE',
        mergeRules: {
            opened: 'RECOMPUTE',
            resolved: 'RECOMPUTE',
            openNow: 'SUM',
            criticalOpenNow: 'SUM',
            vehiclesActive: 'SUM',
            vehiclesRequiringAttention: 'SUM',
            silentVehicles: 'SUM',
            medianResolutionHours: 'NOT_AGGREGATABLE',
        },
        actions: ['VIEW_DASHBOARD', 'COMPARE_PERIOD'],
    },
    'driver.repeat.behaviour': {
        question: 'Which drivers have repeated the same behaviour?',
        examples: [
            'Which drivers keep speeding?',
            'Is anyone speeding repeatedly?',
            'Who has repeat harsh-braking events?',
            'Show me repeat offenders this month',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'INVESTIGATE',
        render: 'TABLE',
        /*
         * TRUE — the first capability for which it is, and the reason D16 exists.
         *
         * An aggregate that narrows to an individual inherits THAT PERSON'S
         * authorization, not the aggregate's: "which driver has the most speeding
         * violations" returns a name to a reader who may be barred from driver
         * detail. So the runner is handed whether the caller may see drivers, and
         * ranks WITHOUT NAMES when they may not.
         *
         * Both v1 roles can already see driver detail, so the redaction never fires
         * today. That is precisely why it is built now (D20): enabling OPERATOR
         * later must stay a toggle rather than becoming a security review.
         */
        identifiesIndividuals: true,
        /*
         * CONCATENABLE and not mergeable, with a caveat worth stating: a driver
         * belongs to one branch, so appending cannot double-count a person. If
         * drivers ever moved between branches the way vehicles now can, this becomes
         * an overcount and must change with it.
         */
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE'],
    },
    'vehicle.health.watchlist': {
        question: 'Which vehicles are showing warning signs?',
        examples: [
            'What should I watch?',
            'Which vehicles might break down?',
            'Which vehicles have fault codes?',
            'Show me the vehicles heading for trouble',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        /*
         * The first capability with an entitlement of its own, and what makes the
         * registry's `feature` field real rather than declared. Every reading it
         * uses comes from CAN telemetry, so without `CAN_TELEMETRY` it must refuse
         * for the entitlement reason — not appear to be broken, and not silently
         * return an empty list, which would read as a fleet in perfect health.
         */
        feature: 'CAN_TELEMETRY',
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE'],
    },
    'fuel.loss.locate': {
        question: 'Where is fuel being lost?',
        examples: ['Where are we losing fuel?', 'Show me fuel loss by location', 'Fuel theft hotspots'],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        /*
         * The LOCATION comes from CAN telemetry, so the capability needs the
         * entitlement even though the incidents themselves do not. Answering without
         * it would mean naming incidents with no location — the part the question
         * actually asks for.
         */
        feature: 'CAN_TELEMETRY',
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE'],
    },
    /*
     * ⚠️ **CONSUMPTION, not EFFICIENCY, and the two are different questions.** Ranking litres puts
     * the biggest vehicles at the top, which is arithmetically right and says nothing about whether
     * any of them is wasteful — a tractor unit beats a van every week. That is what the question
     * asks, so that is what is ranked; the row carries `kmPerL` beside it (through the SAME
     * `economyFigure` CAN operations uses, so the two surfaces cannot disagree) and the narrative
     * names efficiency as the other question rather than answering it silently.
     *
     * ⚠️ **NO COST FIGURE.** There is no fuel price on any model in this platform, so "£ spent" would
     * be invented in the most believable position on the answer — the rule the dashboard's operations
     * band already states about itself.
     */
    'fuel.used.rank': {
        question: 'Which vehicles used the most fuel?',
        examples: [
            'Which vehicles used the most fuel?',
            'Top fuel consumers',
            'Who burned the most diesel this month?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        /*
         * `totalFuelUsedL` is a CAN parameter, so the entitlement is required even though the ranking
         * itself is arithmetic — answering without it would rank an empty set and read as a fleet that
         * burned no fuel.
         */
        feature: 'CAN_TELEMETRY',
        mode: 'ANALYZE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_DASHBOARD'],
    },
    'depot.idle.rank': {
        question: 'Where are vehicles idling most?',
        examples: ['Which sites have the most idling?', 'Where do we waste fuel idling?'],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: 'CAN_TELEMETRY',
        mode: 'ANALYZE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_DASHBOARD'],
    },
    'driver.improvement': {
        question: 'Which drivers are improving or getting worse?',
        examples: ['Who is improving?', 'Which drivers got worse this month?', 'Driver trend'],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'ANALYZE',
        render: 'TABLE',
        // Names a person, so it inherits that person's authorization (D16) exactly
        // as `driver.repeat.behaviour` does.
        identifiesIndividuals: true,
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE'],
    },
    'can.series': {
        question: 'How has a reading moved on this vehicle?',
        examples: [
            'Show me coolant temperature for AV-1042',
            'Battery voltage trend',
            'Fuel level over time',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: 'CAN_TELEMETRY',
        mode: 'ANALYZE',
        render: 'TREND',
        identifiesIndividuals: false,
        // One vehicle, which belongs to one branch.
        scopeShape: 'SUBJECT_SCOPED',
        actions: ['OPEN_VEHICLE'],
    },
    'evidence.attachments': {
        question: 'What evidence is attached to this recommendation?',
        examples: ['Show me the evidence', 'What was attached?', 'Any photos on this one?'],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        scopeShape: 'SUBJECT_SCOPED',
        actions: ['OPEN_RECOMMENDATION'],
    },
    'report.generate': {
        question: 'Which report should I run for this?',
        examples: ['Generate a report', 'Export this as a report', 'I need this for the board'],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: 'SCHEDULED_REPORTS',
        mode: 'REPORT',
        render: 'CARDS',
        identifiesIndividuals: false,
        /*
         * MERGEABLE with an IDENTICAL rule, corrected from SUBJECT_SCOPED.
         *
         * It was declared subject-scoped and takes no subject at all — it lists the
         * platform's template registry, which is the same for every branch of every
         * organisation. `IDENTICAL` is exactly the case
         * `FLEET_SUMMARY_MERGE_RULES` already has a word for: not aggregated,
         * asserted equal across parts rather than assumed. Left as SUBJECT_SCOPED it
         * would have told a parent-tenant reader the answer covered one branch, when
         * it covers all of them equally.
         */
        scopeShape: 'MERGEABLE',
        mergeRules: { templates: 'IDENTICAL' },
        /*
         * It PREPARES and never generates (D12, §7.5). The copilot does not become a
         * second document engine: it names the template that answers the question
         * and links into the reports screen, where a human presses the button that
         * already exists, under the guards and the audit that already apply.
         */
        actions: ['VIEW_REPORTS'],
    },
    /**
     * ⚠️ **A REPORTED QUESTION THAT HAD NO ANSWER** — *"show me all vehicles that
     * have not reported for more than 2 hours"* abstained, because nothing in the
     * registry covered it. The DATA was always there: `FleetSummaryRepository`
     * already computes silence for the dashboard.
     *
     * ⚠️ **It is the distinction between two absences, which is the whole point of
     * the capability.** A vehicle with no incidents is either fine or not
     * reporting, and only silence tells them apart — so this is the question
     * somebody asks when a figure looks suspiciously good.
     */
    'fleet.silent': {
        question: 'Which vehicles have stopped reporting?',
        examples: [
            'Which vehicles are not reporting?',
            'Show me silent vehicles',
            'Which vehicles have gone quiet?',
            'Vehicles that stopped sending telemetry',
            'Which vehicles have not reported for 2 hours?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        /*
         * ⚠️ Gated on TELEMETRY rather than left ungated: a tenant who buys no
         * telemetry has nothing that could be silent, so the honest answer is that
         * the question does not apply rather than an empty list — which would read
         * as a fleet reporting perfectly.
         */
        feature: 'TELEMETRY',
        mode: 'INVESTIGATE',
        render: 'TABLE',
        identifiesIndividuals: false,
        /*
         * CONCATENABLE: a silent vehicle belongs to exactly one branch, so the lists
         * append with a branch column. Merging would need a single figure, and "11
         * silent" across three branches is not one somebody can act on without
         * knowing whose.
         */
        scopeShape: 'CONCATENABLE',
        actions: ['OPEN_VEHICLE', 'VIEW_DASHBOARD'],
    },
    'ops.overnight.brief': {
        question: 'What changed overnight?',
        examples: [
            'What happened overnight?',
            'What changed since yesterday?',
            'Morning briefing',
            'What moved while I was away?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'ANALYZE',
        render: 'KPI',
        identifiesIndividuals: false,
        /*
         * CONCATENABLE rather than mergeable: every figure is a count of things
         * that happened in one branch, and a vehicle, an incident and a
         * recommendation each belong to exactly one. Adding them is exact.
         */
        scopeShape: 'CONCATENABLE',
        actions: ['VIEW_INCIDENT_QUEUE', 'VIEW_RECOMMENDATION_QUEUE'],
    },
    'ops.period.compare': {
        question: 'How does this period compare with the one before it?',
        examples: [
            'Is this week better or worse than last?',
            'Compare the last 30 days with the previous 30',
            'What changed since last month?',
        ],
        roles: ['TENANT_ADMIN', 'OPERATIONS_MANAGER'],
        feature: null,
        mode: 'ANALYZE',
        render: 'KPI',
        identifiesIndividuals: false,
        scopeShape: 'MERGEABLE',
        mergeRules: {
            opened: 'RECOMPUTE',
            resolved: 'RECOMPUTE',
            // The CHANGE is recomputed from merged totals, never averaged across
            // branches: averaging percentages weights a branch with four incidents
            // the same as one with four hundred.
            openedChangePct: 'RECOMPUTE',
            resolvedChangePct: 'RECOMPUTE',
        },
        actions: ['VIEW_DASHBOARD'],
    },
};
/**
 * The modes worth showing, derived from what actually exists.
 *
 * §10 asks for a test asserting no mode ships empty. Deriving the strip is
 * stronger than testing a hand-written list: an empty tab is not possible to
 * render rather than merely asserted against. `Automate` is hidden today for
 * exactly this reason, and appears the moment it has a capability.
 */
export function availableCopilotModes(capabilities = COPILOT_CAPABILITIES) {
    const present = new Set(Object.values(capabilities).map((c) => c.mode));
    return COPILOT_MODES.filter((m) => present.has(m));
}
// ── parameters ──────────────────────────────────────────────────────────────
export const vehicleRiskExplainParamsSchema = z.object({ vehicleId: z.string().uuid() }).strict();
export const recommendationExplainParamsSchema = z
    .object({ recommendationId: z.string().uuid() })
    .strict();
/**
 * A window in hours, bounded.
 *
 * Bounded rather than open because an unbounded window is a table scan somebody
 * asked for by accident, and the default matches the dashboard's so the copilot
 * and the screen answer the same question the same way.
 */
/**
 * A named CAN reading over a window.
 *
 * The parameter is an ENUM, not free text. A capability taking a column name as
 * a string is text-to-SQL wearing a different coat — D4 — and the list is the
 * readings a trend is meaningful for.
 */
export const CAN_SERIES_FIELDS = [
    'fuelLevelPct',
    'batteryVoltageV',
    'engineCoolantTempC',
    'vehicleSpeedKph',
    'odometerKm',
    'engineRunHours',
];
export const canSeriesFieldSchema = z.enum(CAN_SERIES_FIELDS);
/**
 * Which of the series readings a vehicle has ACTUALLY sent.
 *
 * ⚠️ The picker offered the fixed six regardless of the vehicle, defaulting to
 * fuel level — so on an electric van, or a device reporting eight of the
 * fifty-one canonical parameters, the default question was one that could never
 * be answered. Offering what the DEVICE supports rather than what the schema
 * supports is the fix, and it is a fact about a vehicle rather than about a
 * tenant, so it is asked per vehicle.
 */
export const reportedSeriesFieldsSchema = z.object({
    vehicleId: z.string().uuid(),
    registration: z.string().nullable(),
    /** Readings with at least one value in the window, in catalogue order. */
    reported: z.array(canSeriesFieldSchema),
    /**
     * Every sample in the window, whether or not it carried a series reading.
     *
     * Distinguishes "this vehicle has no such sensor" from "this device has
     * stopped" — two absences the platform keeps apart everywhere else, and which
     * a caller must be able to tell apart before it says either.
     */
    samplesRead: z.number().int(),
    hours: z.number().int(),
});
export const CAN_SERIES_LABELS = {
    fuelLevelPct: 'Fuel level (%)',
    batteryVoltageV: 'Battery (V)',
    engineCoolantTempC: 'Coolant temperature (°C)',
    vehicleSpeedKph: 'Speed (km/h)',
    odometerKm: 'Odometer (km)',
    engineRunHours: 'Engine hours',
};
export const canSeriesParamsSchema = z
    .object({
    vehicleId: z.string().uuid(),
    field: canSeriesFieldSchema,
    hours: z.coerce.number().int().min(1).max(2160).default(168),
})
    .strict();
const windowParams = z
    .object({ hours: z.coerce.number().int().min(1).max(2160).default(168) })
    .strict();
/**
 * How long a vehicle must have been quiet to count as silent.
 *
 * ⚠️ **NOT called `hours`, and the name is the point.** Everywhere else `hours`
 * is *the window to look at*; here the number is *how long the silence has
 * lasted* — a different quantity. Reusing the name would be the
 * `durationMs`/`durationSeconds` mistake this platform already records: one word,
 * two meanings, a thousandfold apart in that case and merely wrong in this one.
 *
 * ⚠️ **The default is DERIVED from the dashboard's own constant, never the
 * literal 24.** The summary and the daily headline already say *"N vehicles have
 * sent nothing for over 24 hours"*, and a copilot answering the same question at
 * a different threshold would give two correct figures that disagree with nothing
 * on screen explaining why. Deriving it means they cannot drift.
 *
 * ⚠️ **But it MUST be overridable, because four notions of "quiet" already
 * exist** — this constant, the fleet map's fixed 24-hour position window, the
 * CONNECTIVITY anomaly family's daily baseline, and the two hours a customer
 * actually asked about. A capability that hard-coded any one of them would answer
 * a different question from the one asked while looking correct.
 */
export const silenceParamsSchema = z
    .object({
    silentForHours: z.coerce
        .number()
        .int()
        .min(1)
        .max(720)
        .default(FLEET_SUMMARY_SILENT_AFTER_HOURS),
})
    .strict();
/** Validated before anything runs — the registry's whole point. */
export const COPILOT_CAPABILITY_PARAMS = {
    /*
     * ⚠️ The document itself, validated by Zod before anything runs — and then by
     * `validateQuery` against the catalogue, which is the half Zod cannot do. The
     * shape says it is well-formed; the catalogue says whether the question is
     * askable at all.
     */
    'fleet.query': queryDocumentSchema,
    'vehicle.risk.explain': vehicleRiskExplainParamsSchema,
    'recommendation.explain': recommendationExplainParamsSchema,
    'attention.today': windowParams,
    'incidents.critical.today': windowParams,
    'ops.period.summary': windowParams,
    'ops.period.compare': windowParams,
    'driver.repeat.behaviour': windowParams,
    'vehicle.health.watchlist': windowParams,
    'fuel.loss.locate': windowParams,
    'fuel.used.rank': windowParams,
    'depot.idle.rank': windowParams,
    'driver.improvement': windowParams,
    'report.generate': windowParams,
    'fleet.silent': silenceParamsSchema,
    'ops.overnight.brief': windowParams,
    'can.series': canSeriesParamsSchema,
    'evidence.attachments': recommendationExplainParamsSchema,
};
// ── findings ────────────────────────────────────────────────────────────────
export const copilotEvidenceDtoSchema = z.object({
    id: z.string().uuid(),
    kind: copilotEvidenceKindSchema,
    /** Null when the record has been archived or removed since the finding was taken. */
    refId: z.string().uuid().nullable(),
    label: z.string(),
    detail: z.string().nullable(),
    ordinal: z.number().int(),
    /**
     * True when the referenced record can no longer be opened.
     *
     * The row still renders and still says what it said (D22): a snapshot that
     * quietly lost its evidence is worse than one saying the evidence moved.
     */
    unavailable: z.boolean(),
});
/**
 * The four facts under every finding (D2). Not three, and not a score.
 *
 * Each is checkable against a record, which is the property a confidence
 * percentage does not have.
 */
/**
 * How the capability that answered was CHOSEN.
 *
 * ⚠️ Separate from `determinism`, and the difference matters. `determinism`
 * says how the FIGURES were produced — computed, reworded, projected — and a
 * routed question changes none of that: the numbers are still calculated by
 * OIP and a model still never reaches the database. What a model may now do is
 * decide WHICH declared question a typed sentence meant, and the reader should
 * be told when it did, because a mis-read question produces a perfectly correct
 * answer to something they did not ask.
 *
 * - `DIRECT` — the reader clicked the question. Nothing was interpreted.
 * - `MATCHED` — resolved from typed text by the deterministic matcher.
 * - `MODEL` — the deterministic matcher could not decide and a model chose from
 *   the declared catalogue.
 */
/**
 * Re-exported under its original name; the vocabulary now lives with the rest of
 * G5's provenance layers, which is where it always belonged.
 */
export const copilotRoutingSchema = queryRoutingSchema;
export const copilotProvenanceSchema = z.object({
    determinism: copilotDeterminismSchema,
    /** Which tenants the answer covers. Never an unqualified "the fleet". */
    scopeLabel: z.string(),
    scopeTenantIds: z.array(z.string().uuid()),
    /** The instant the read was taken. */
    asOf: z.string().datetime(),
    /** What the answer could NOT see. Null when the capability declares no blind spot. */
    coverageLabel: z.string().nullable(),
    coverageSeen: z.number().int().nullable(),
    coverageTotal: z.number().int().nullable(),
    /**
     * How the capability was chosen. `DIRECT` on every finding written before
     * this existed, which is true of them: the deck was the only way to ask.
     */
    routing: copilotRoutingSchema.default('DIRECT'),
    /**
     * What the reader typed, when it was interpreted. Null for a deck click.
     *
     * Kept so the strip can say "read from: <their words>" — a reader shown that
     * their sentence became a different question can correct it, and without the
     * original there is nothing to compare the canonical question against.
     */
    routedFrom: z.string().nullable().default(null),
});
export const copilotFindingDtoSchema = z.object({
    id: z.string().uuid(),
    capabilityKey: z.string(),
    /** The capability's canonical phrasing, so the reader sees how it was read. */
    question: z.string(),
    params: z.record(z.unknown()),
    render: copilotRendererSchema,
    /**
     * The wording the reader should see.
     *
     * Model wording when there is any, the deterministic sentence otherwise — the
     * client is never asked to choose, because a client that got the rule wrong
     * would silently show one finding in the model's voice and the next in the
     * platform's.
     */
    headline: z.string(),
    narrative: z.string(),
    /**
     * The deterministic original, always present.
     *
     * Sent even when it IS what `headline` holds, so "show me what the platform
     * said" is answerable without a second request — and so a reader can compare
     * the model's phrasing against the computed one.
     */
    deterministicHeadline: z.string(),
    deterministicNarrative: z.string(),
    /** The investigation this finding belongs to. Null for findings taken before they existed. */
    investigationId: z.string().uuid().nullable(),
    /** Pinned to the top of its investigation — a conclusion, not a step. */
    pinned: z.boolean(),
    /** Cleared from the workspace. The opposite of pinned, never both. */
    dismissed: z.boolean().default(false),
    /** NONE | PENDING | REWORDED | UNAVAILABLE. */
    wordingStatus: z.string(),
    /** Why wording did not happen. Null when it did, or was never attempted. */
    wordingRefusal: z.string().nullable(),
    data: z.record(z.unknown()),
    provenance: copilotProvenanceSchema,
    evidence: z.array(copilotEvidenceDtoSchema),
    createdAt: z.string().datetime(),
});
/**
 * Ask by capability and parameters, not by free text.
 *
 * Phase 2 has no natural-language routing: the caller names the capability. That
 * is not a placeholder for a missing model — it is the interface the model will
 * eventually call, so the whole path is exercised and audited before any
 * wording layer exists.
 */
export const copilotAskRequestSchema = z
    .object({
    capabilityKey: copilotCapabilityKeySchema,
    params: z.record(z.unknown()),
    /**
     * How the capability was chosen, and what the reader typed if it was
     * interpreted.
     *
     * ⚠️ Reported by the client because only the client knows — the server sees
     * a capability key either way and cannot tell a deck click from a resolved
     * sentence. It is DISCLOSURE, never authorization: it changes what the
     * provenance strip says and nothing about what may run, so a caller lying
     * about it gains nothing. Defaulting to `DIRECT` keeps every existing
     * integration correct, because the deck was the only way to ask.
     */
    routing: copilotRoutingSchema.default('DIRECT'),
    routedFrom: z.string().trim().min(1).max(400).nullable().default(null),
})
    .strict();
/**
 * A question to route.
 *
 * ⚠️ The reader's words go to the server here, which the ask path deliberately
 * avoids — a model cannot choose a capability without them. They are held for
 * the life of one execution and cleared on its terminal write (D17), and no
 * capability list is accepted from the client: what this reader may run is
 * resolved from the role the guards established.
 */
export const copilotRouteRequestSchema = z
    .object({
    question: z.string().trim().min(1).max(400),
    /**
     * The reader has SEEN how their words were read and said no.
     *
     * ⚠️ **The matcher can win on a NEAR MISS and there is no threshold that
     * separates one from a legitimate paraphrase — measured, not assumed.** Over a
     * corpus of phrasings the lowest correct match and the highest wrong pre-empt
     * both landed on **0.63**: *"which lorries went quiet"* (right) and *"which
     * driver has the most incidents today"* (wrong, answered with a critical-incident
     * list). A confidence band tuned between them would break the first to fix the
     * second. The gap to the runner-up does not separate them either — a question
     * naming two subjects usually leaves only ONE capability above threshold, so
     * there is no runner-up to compare against.
     *
     * So the reader decides, which they can and the scorer cannot: the chip shows the
     * reading BEFORE the question runs, and this is how they reject it.
     *
     * ⚠️ **It GRANTS NOTHING.** Skipping the matcher reaches the model path, which is
     * behind the same entitlement, the same role filter and the same
     * `validateRoutedCapability` gate — and a caller could reach it anyway by typing
     * a question that happens not to match. It changes which path runs, never what
     * may run, so a client setting it is making a request about ITS OWN reading.
     */
    skipMatch: z.boolean().default(false),
    /**
     * The period the reader had already chosen when they asked.
     *
     * ⚠️ **WITHOUT THIS THE MODEL GUESSES A PARAMETER THE READER HAD SET, and measured it is the
     * dominant failure class.** `windowHours` is part of a QueryDocument, so a composing model had
     * to infer the period from the sentence — while the copilot rail carries a window control and
     * this request carried only `question` and `skipMatch`. Two corpus runs: 5 of 6 non-abstention
     * failures were the window (`Expected number, received null` ×4, `WINDOW_REQUIRED`,
     * `WINDOW_NOT_APPLICABLE`), and in another every one of 11 refusals was `WINDOW_REQUIRED`.
     *
     * ⚠️ **IT COMPLETES, IT NEVER OVERRIDES.** A model that DID supply a window keeps it: the
     * reader's words ("in the last day") are more specific than a rail they may not have looked at,
     * and silently replacing 24 with 168 is the near miss this whole surface is built to avoid. The
     * platform fills in only where the document omits one on a grain that requires it — which is
     * precisely the case that refuses today.
     *
     * ⚠️ **No new disclosure rides with it, and that is a conclusion rather than an omission.** The
     * value filled in IS the one the reader chose and can see on their own control, so the document
     * shown in the COMPOSED panel makes no claim they cannot check. What WOULD need disclosing is
     * filling from a default they never saw, which this deliberately does not do.
     *
     * Optional because an API caller has no rail; absent, routing behaves exactly as before.
     */
    windowHours: z
        .number()
        .int()
        .positive()
        .max(24 * 366)
        .optional(),
})
    .strict();
/**
 * Why a round-trip produced no route.
 *
 * ⚠️ **Every one of these degrades identically at the USER boundary and must
 * stay distinguishable OPERATIONALLY.** A reader gets the same honest abstention
 * whether the model was unavailable, slow, malformed or inventive — that is
 * G11 — but somebody answering "why is routing not working" needs to tell a
 * missing worker from a model that keeps proposing capabilities the registry
 * refuses. Those are different fixes, and a single `FAILED` would hide which.
 *
 * A CLOSED list, so a new failure mode is a decision rather than a new string
 * appearing in a log.
 */
export const ROUTING_FAILURE_CODES = [
    /** Nothing consumed the job, or Redis was unreachable. */
    'ROUTER_UNAVAILABLE',
    /** No model router is registered — the state until step 6 part 2 lands. */
    'NO_ROUTER_CONFIGURED',
    /** The API's bounded wait ended. The execution may still succeed. */
    'BUDGET_EXPIRED',
    /** The provider did not answer in time. */
    'MODEL_TIMEOUT',
    /** The response was not the declared output shape. */
    'MALFORMED_OUTPUT',
    /** It named a capability that does not exist — G1's floor. */
    'CAPABILITY_NOT_DECLARED',
    /** It named a parameter the capability never declared. */
    'PARAMETER_NOT_DECLARED',
    /** A declared parameter, a value its own schema refused. */
    'PARAMETER_INVALID',
    /** Declared and real, and not something THIS reader may run. */
    'NOT_AVAILABLE_TO_THIS_READER',
    /** The allowance is spent. Degrades, never errors (D21). */
    'BUDGET_EXHAUSTED',
    /**
     * A COMPOSED query parsed as a document and cannot be executed.
     *
     * ⚠️ **Distinct from `PARAMETER_INVALID`, because the fix is different.** That one
     * is a shape error — a string where a number belongs — and means the model
     * misread the schema. This one is a document whose every field is real and whose
     * COMBINATION is refused: a measure outside its grain, an aggregation the field
     * does not permit, a window on a grain that has none. The model read the
     * catalogue correctly and composed something the catalogue does not allow, which
     * is usually a gap in what the PROMPT told it rather than a malfunction.
     *
     * Folding the two together would hide which, and they are the two signals that
     * say whether the prompt or the schema needs work.
     */
    'QUERY_NOT_ASKABLE',
    /** The router understood and declined — a first-class answer (G4). */
    'NOT_ROUTED',
];
export const routingFailureCodeSchema = z.enum(ROUTING_FAILURE_CODES);
export const copilotFindingListSchema = z.object({
    items: z.array(copilotFindingDtoSchema),
    /**
     * How many the reader has CLEARED from this workspace.
     *
     * ⚠️ Returned always, so the way back can name a number. Clearing must never
     * be a one-way door: without a standing "N cleared — show them", somebody who
     * tidied too enthusiastically has lost their work and no reason to believe it
     * still exists. The same rule the question deck states about hiding.
     */
    dismissedCount: z.number().int().default(0),
});
/**
 * The shape `vehicle.risk.explain` puts in `data`.
 *
 * Declared so the renderer is typed rather than reading loose JSON, and so a
 * change to the runner that drops a field fails a test instead of blanking a
 * card.
 */
export const vehicleRiskExplainDataSchema = z.object({
    vehicleId: z.string().uuid(),
    registration: z.string(),
    name: z.string(),
    /** Highest current risk among unresolved incidents, or null when there are none. */
    riskLevel: z.string().nullable(),
    /** The score behind that level. Null for the same reason. */
    riskScore: z.number().int().nullable(),
    openIncidents: z.number().int(),
    outstandingRecommendations: z.number().int(),
    /** The scoring factors, in descending order of impact. */
    factors: z.array(z.object({
        name: z.string(),
        reason: z.string(),
        scoreImpact: z.number(),
    })),
});
/** `attention.today` and `incidents.critical.today` both render a TABLE. */
export const copilotTableDataSchema = z.object({
    /** Column keys, in display order. The renderer never guesses the order. */
    columns: z.array(z.object({ key: z.string(), label: z.string() })),
    rows: z.array(z.record(z.union([z.string(), z.number(), z.null()]))),
    /**
     * Rows the capability found but did not return.
     *
     * Stated rather than silently truncated: a list showing ten of forty with no
     * note reads as a complete answer, which is the "sample presented as a total"
     * failure this platform has already had once.
     */
    omitted: z.number().int(),
});
/** `ops.period.summary` and `ops.period.compare` both render KPIs. */
export const copilotKpiDataSchema = z.object({
    kpis: z.array(z.object({
        key: z.string(),
        label: z.string(),
        /** Null is "not available", never zero — the same rule the briefing holds. */
        value: z.number().nullable(),
        /** Change against the previous period, when one can honestly be computed. */
        changePct: z.number().nullable(),
        /** Why a value or a change is absent. Null when it is present. */
        unavailable: z.string().nullable(),
    })),
});
/** `recommendation.explain` renders CARDS. */
export const recommendationExplainDataSchema = z.object({
    recommendationId: z.string().uuid(),
    action: z.string(),
    actionCategory: z.string(),
    status: z.string(),
    riskLevel: z.string(),
    riskScore: z.number().int(),
    approvalRequired: z.boolean(),
    registration: z.string().nullable(),
    policyName: z.string().nullable(),
    /**
     * The deterministic reasoning steps recorded when the recommendation was
     * generated — before any model ran, which is why they can be shown as the
     * explanation rather than as a summary of one.
     */
    reasoningSteps: z.array(z.string()),
    /** Which wording the recommendation itself carries: DETERMINISTIC or LLM. */
    explanationSource: z.string(),
});
// ── investigations ──────────────────────────────────────────────────────────
/**
 * A named investigation — the container that turns a feed of findings into a
 * workspace.
 *
 * Archived rather than deleted, like everything else the platform holds: an
 * investigation records what somebody looked into, and losing that is worse
 * than a cluttered list.
 */
export const COPILOT_INVESTIGATION_STATUSES = ['OPEN', 'RESOLVED', 'ARCHIVED'];
export const copilotInvestigationStatusSchema = z.enum(COPILOT_INVESTIGATION_STATUSES);
export const copilotInvestigationDtoSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: copilotInvestigationStatusSchema,
    findingCount: z.number().int(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    archivedAt: z.string().datetime().nullable(),
});
export const updateCopilotInvestigationSchema = z
    .object({
    // Trimmed and non-empty: an investigation with a blank name is one nobody
    // can find again, which defeats the object of naming it at all.
    name: z.string().trim().min(1).max(120).optional(),
    status: copilotInvestigationStatusSchema.optional(),
})
    .strict()
    .refine((v) => v.name !== undefined || v.status !== undefined, {
    message: 'Nothing to change',
});
/**
 * A name derived from the first finding, so an investigation is never "Untitled".
 *
 * Asking for a title before any work exists is why most workspace products fill
 * up with folders called "test". The subject and the day are what somebody
 * actually searches for a week later.
 */
export function defaultInvestigationName(capabilityKey, subjectLabel, at) {
    const c = COPILOT_CAPABILITIES[capabilityKey];
    const topic = c ? c.question.replace(/\?$/, '') : capabilityKey;
    const day = at.toISOString().slice(0, 10);
    return [topic, subjectLabel, day].filter(Boolean).join(' · ').slice(0, 120);
}
/**
 * Whether two findings may be compared side by side.
 *
 * Only the SAME capability, because comparing a risk explanation with a period
 * summary is subtracting two unrelated shapes. Everything else — different
 * subjects, different windows, different branches, different instants — is a
 * legitimate comparison and is exactly what the reader wants.
 */
export function comparable(a, b) {
    return a.id !== b.id && a.capabilityKey === b.capabilityKey;
}
/**
 * Asking the platform what a typed question means, before running it.
 *
 * ⚠️ A separate call from `ask`, deliberately. The reader CONFIRMS the reading
 * before anything executes — the rule `copilot-resolve.ts` already states, and
 * the only moment a wrong interpretation is cheap to correct. Folding routing
 * into `ask` would run the model's guess without showing it.
 */
export const copilotResolveRequestSchema = z
    .object({ text: z.string().trim().min(1).max(400) })
    .strict();
export const copilotResolveResponseSchema = z.object({
    /** Null when nothing could be resolved — an abstention, not a failure. */
    capabilityKey: z.string().nullable(),
    /** The canonical question, so the reader can see how their words were read. */
    question: z.string().nullable(),
    routing: copilotRoutingSchema,
    /**
     * Why, in one sentence, when a model chose or abstained.
     *
     * ⚠️ Never a confidence PERCENTAGE. A number like "87% sure" cannot be
     * derived from anything OIP holds and would be a fabrication in the most
     * authoritative position on the screen — §0.2, settled. A sentence can be
     * argued with; a score invites being trusted.
     */
    reason: z.string().nullable(),
    /**
     * Other readings worth offering, best first. The deterministic matcher
     * produces these; a model answer carries none, because "the second thing a
     * model would have said" is not a ranking anybody can check.
     */
    alternatives: z.array(z.object({ capabilityKey: z.string(), question: z.string() })),
});
/**
 * What this tenant has asked the copilot, and what it was refused.
 *
 * ⚠️ **Grouped, never listed.** A question is attributed to a PERSON, and a
 * screen naming who asked what would turn a usage report into surveillance of
 * colleagues. Counts per capability answer the product question — which
 * questions earn their place — without naming anybody, and `promptText` stays
 * null by default (D17) so nobody's words are retained either.
 */
export const copilotUsageResponseSchema = z.object({
    windowDays: z.number().int(),
    /** Every question asked in the window, refused or not. */
    total: z.number().int(),
    byCapability: z.array(z.object({
        capabilityKey: z.string(),
        asked: z.number().int(),
        /**
         * Answered, but with nothing in it.
         *
         * ⚠️ Counted apart from `asked`, and that is the useful half. A capability
         * asked forty times that returns empty every time is either a question
         * nobody should be offered or a fleet in perfect health — opposite
         * conclusions, which one "asked" number merges and presents as demand.
         */
        emptyAnswers: z.number().int(),
    })),
    /** Why questions were refused. `UNRESOLVED` means nothing could answer it. */
    byRefusal: z.array(z.object({ refusal: z.string(), count: z.number().int() })),
    /**
     * ⚠️ Capabilities this tenant may run and has NEVER asked in the window.
     *
     * The half a usage table cannot show by being a table of usage. A question
     * nobody asks is the candidate for removal, and it is invisible in a list
     * ordered by how often things were asked.
     */
    neverAsked: z.array(z.string()),
});
/** Recording that a typed question resolved to nothing. */
export const copilotUnresolvedRequestSchema = z.object({}).strict();
/**
 * Turn a model's PROPOSAL into an authoritative route, or a named refusal.
 *
 * ⚠️ **G1's validation boundary, and it lives in contracts because this is where
 * the registry and the parameter schemas are.** A model may CHOOSE a declared
 * capability and FILL declared typed parameters; it may never author a
 * capability, a field, an operator or a query. Nothing it returns is
 * authoritative until every check below has passed.
 *
 * ⚠️ **The failure codes are distinct because the FIXES are.** A capability that
 * does not exist is a model inventing; a parameter that does not belong is a
 * model over-reaching; a value its own schema refuses is a model guessing. One
 * `MALFORMED_OUTPUT` for all three would leave somebody unable to tell which,
 * while the reader — correctly — sees the same abstention either way (G11).
 *
 * ⚠️ **Abstention passes STRAIGHT THROUGH as a success.** Declining is an answer
 * (G4), and recording it as a failure would make a well-behaved model look
 * broken and hide the models that actually are.
 */
/**
 * Whether this capability's PARAMETERS are a whole query document.
 *
 * ⚠️ **ONE derivation, THREE readers, and that is the point.** It decides the
 * semantic gate in `validateRoutedCapability` below, and it decides whether a
 * routed answer is reported as `MODEL` or `COMPOSED`. Those two must never
 * disagree: a document strict enough to need `validateQuery` is exactly a document
 * whose question nobody wrote, and a copy of this rule in the API would eventually
 * say one and not the other — so a reader would be told a model "matched" their
 * sentence while it had in fact built the question.
 *
 * ⚠️ **Reference equality against the imported schema, never `key === 'fleet.query'`.**
 * A second document-taking capability added later is covered without being named —
 * and naming would be the silent failure, because its documents would still parse.
 */
export function takesAQueryDocument(key) {
    return COPILOT_CAPABILITY_PARAMS[key] === queryDocumentSchema;
}
export function validateRoutedCapability(proposal, availableKeys, 
/**
 * What the READER had chosen, where they had a control to choose it on.
 *
 * ⚠️ **The gate COMPLETES here rather than a caller repairing afterwards, and that is the
 * argument its own comment already makes about itself**: it is the one place that reasons about a
 * grain's window rule, so a repair sitting outside it would be a second opinion on what a model
 * may return — and the two would eventually disagree about the case that matters. The worker's
 * comment says the same of the validator as a whole: *"a second implementation here would be a
 * second opinion."*
 */
readerWindowHours) {
    if (!proposal.capabilityKey) {
        return { status: 'SUCCEEDED', abstentionReason: proposal.reason ?? 'NOT_ROUTED' };
    }
    const key = proposal.capabilityKey;
    /* The floor: a key nobody wrote is a capability invented. */
    if (!COPILOT_CAPABILITY_KEYS.includes(key)) {
        return { status: 'FAILED', failureCode: 'CAPABILITY_NOT_DECLARED' };
    }
    /*
     * Reachable here and now. A capability the tenant's plan excludes is real and
     * not theirs, and routing to it would produce an offer that refuses itself.
     */
    if (!availableKeys.includes(key)) {
        return { status: 'FAILED', failureCode: 'NOT_AVAILABLE_TO_THIS_READER' };
    }
    /*
     * ⚠️ **`windowHours: null` IS TREATED AS ABSENT, and without this the window completion could never
     * fire for the shape the model actually produces.**
     *
     * MEASURED: across one session every single one of **14 failures was
     * `windowHours: Expected number, received null`** — the model writing `null` where the instruction
     * is to omit the key. The schema is `.optional()` rather than `.nullable()`, so `null` is refused
     * by the SHAPE before any completion runs: on an untimed grain a perfectly good document is thrown
     * out, and on a timed one the reader's own window never gets its chance.
     *
     * ⚠️ **NORMALISING, not authoring.** `null` here has exactly one meaning — "no window" — and the
     * prompt already asks for the key to be omitted instead. This is the same act as the completions
     * below: the platform reading an omission the model expressed badly. It is done HERE rather than by
     * loosening `queryDocumentSchema` to `.nullish()`, because that schema is what `/api/v1/query`
     * publishes to integrators and `null` should stay refused there — a client sending it is making a
     * mistake somebody should be told about, where a model doing it is a fact of life.
     *
     * Narrow on purpose: ONE key, and only where it is exactly `null`.
     */
    const proposedParams = typeof proposal.params === 'object' &&
        proposal.params !== null &&
        'windowHours' in proposal.params &&
        proposal.params.windowHours === null
        ? Object.fromEntries(Object.entries(proposal.params).filter(([k]) => k !== 'windowHours'))
        : proposal.params;
    /*
     * The capability's OWN schema. Every one is `.strict()`, so an undeclared
     * parameter name is refused by the schema rather than by a list here that
     * would need keeping in step with sixteen of them.
     */
    const parsed = COPILOT_CAPABILITY_PARAMS[key].safeParse(proposedParams);
    if (!parsed.success) {
        const unrecognised = parsed.error.issues.some((i) => i.code === 'unrecognized_keys');
        return {
            status: 'FAILED',
            failureCode: unrecognised ? 'PARAMETER_NOT_DECLARED' : 'PARAMETER_INVALID',
            /*
             * Capped, because a `.strict()` schema over a document with three arrays can produce a great
             * many issues and a log line is not the place for all of them. The first few name the path,
             * which is what a prompt fix needs.
             */
            detail: parsed.error.issues
                .slice(0, 4)
                .map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
                .join('; '),
        };
    }
    /*
     * ⚠️ **A DOCUMENT THAT PARSES IS NOT A DOCUMENT THAT RUNS, and this boundary was
     * missing it.**
     *
     * `queryDocumentSchema` is the SHAPE; `validateQuery` is the SEMANTICS — 14
     * refusal codes covering grain reachability, which aggregations a field permits,
     * window rules, and a document that names a tenant. Without this check a model
     * could return a `fleet.query` document that parsed, be recorded as a SUCCEEDED
     * routing decision, and then throw at execution — so the model's output would
     * have passed G1's boundary and failed later, which is exactly what this
     * function's own comment says must not happen: *nothing is authoritative until
     * every one of these passes*.
     *
     * ⚠️ **DERIVED from the params schema, never a hard-coded `key === 'fleet.query'`.**
     * Any capability whose parameters ARE a query document gets the semantic check,
     * so a second one added later cannot slip past by not being named here. Reference
     * equality against the imported constant is what makes that exact rather than a
     * guess about shape.
     */
    if (takesAQueryDocument(key)) {
        /*
         * ⚠️ **COMPLETING the document with a parameter the model was never given.**
         *
         * The reader's window is an INPUT to composition that never reached the prompt, so a model
         * omitting it on a timed grain is not a model getting something wrong — it is being asked to
         * infer a value somebody had already stated. `validateQuery`'s own reason for refusing is that
         * *"without one the answer covers all of history, which is never what somebody means"*, and
         * filling from the reader's own control satisfies exactly that concern.
         *
         * ⚠️ **ONLY when the document omits one, ONLY on a grain that requires one, and NEVER over a
         * value the model supplied.** Each of those is a silent wrong answer inverted: overriding would
         * replace a period the reader named in words; filling on an untimed grain would earn
         * `WINDOW_NOT_APPLICABLE` and turn a good document into a refused one; filling when nothing was
         * asked for would invent a period on a caller who has no control to have chosen it.
         */
        const document = parsed.data;
        const timedGrain = document.grain in CATALOGUE_GRAIN_TIME;
        const withWindow = timedGrain && document.windowHours === undefined && (readerWindowHours ?? 0) > 0
            ? { ...document, windowHours: readerWindowHours }
            : document;
        /*
         * ⚠️ **COMPLETING THE VEHICLE A PER-VEHICLE READING REQUIRES — and this is a DIFFERENT KIND of
         * completion from the window, which is why it is disclosed differently.**
         *
         * The window filled a value the READER had stated on their own control. This adds STRUCTURE the
         * reader did not state — so the panel's sentence had to stop saying a model built the whole
         * document. What justifies it is that the document is INVALID without it: `acrossVehicles ===
         * false` means the measure has no fleet-wide value at all, the catalogue's own advice is *"ask
         * for it per vehicle"*, and the alternative is refusing a document whose grain, measure,
         * aggregation and window are all right.
         *
         * ⚠️ **MEASURED — this is the composer's one persistent failure, one requirement from correct.**
         * On the narrowed catalogue the model reaches grain `vehicle-hour` AND `reading.totalFuelUsedL`
         * and omits only this: **10 of 11 refusals across 120 attempts were
         * `READING_NEEDS_A_VEHICLE`.** Before narrowing it composed a count of vehicles instead.
         *
         * ⚠️ **A GROUPING, so it is a SUPERSET rather than a different question.** If the reader meant
         * one named vehicle and the model dropped the name, this answers per-vehicle across the fleet —
         * broader than asked, containing the row they wanted, and visible in the document. A FILTER would
         * be narrower and would have to invent WHICH vehicle, which is the one thing it must not do.
         *
         * ⚠️ **`uniqueVehiclePin()`, never a literal, never `[0]`, and never "any vehicle dimension".**
         * `vehicle.status` and `vehicle.class` are vehicle dimensions that group MANY vehicles, so
         * completing with one would reinstate the average the refusal exists to prevent. And it must be
         * the UNIQUE pin: `Vehicle.name` is an accepted pin carrying no unique index, so completing with
         * a name would merge the 23 development vehicles that share 13 names into ten wrong rows — the
         * platform choosing an answer it knows to be ambiguous. It read `[0]` under a comment saying the
         * unique one is first; a property carried by POSITION is one a later edit reorders silently.
         *
         * An absent pin completes NOTHING rather than inventing one, and the document is then refused by
         * `validateQuery` with the reason the reader can act on.
         */
        const needsAVehicle = withWindow.measures.some((m) => {
            /* The catalogue's own indexed accessor, not a scan — and not a second copy of the rule. */
            const entry = catalogueEntry(m.field);
            return entry?.kind === 'MEASURE' && entry.acrossVehicles === false;
        });
        const pins = VEHICLE_PINNING_DIMENSIONS.map((d) => d.field);
        const alreadyPinned = withWindow.dimensions.some((d) => pins.includes(d)) ||
            withWindow.filters.some((f) => pins.includes(f.field));
        /*
         * ⚠️ Refused rather than completed when the document is already at the dimension cap: replacing
         * one the model chose would be overriding, which is the rule the window completion also keeps.
         */
        const room = withWindow.dimensions.length < MAX_QUERY_DIMENSIONS;
        const pin = uniqueVehiclePin();
        const completed = needsAVehicle && !alreadyPinned && room && pin !== undefined
            ? { ...withWindow, dimensions: [...withWindow.dimensions, pin] }
            : withWindow;
        const semantic = validateQuery(completed);
        if (!semantic.ok) {
            return {
                status: 'FAILED',
                failureCode: 'QUERY_NOT_ASKABLE',
                // The refusal CODES and their own wording — which already say what a reader should ask instead.
                detail: semantic.refusals
                    .slice(0, 4)
                    .map((r) => `${r.code}${r.field ? ` (${r.field})` : ''}: ${r.message}`)
                    .join('; '),
            };
        }
        /*
         * ⚠️ The COMPLETED document is what flows out, or the fill would be judged and then discarded —
         * the gate approving one thing and the executor running another. Reported so the worker can log
         * that the platform supplied the period rather than the model.
         */
        return {
            status: 'SUCCEEDED',
            selectedCapability: key,
            validatedParams: completed,
            windowCompleted: withWindow !== document,
            dimensionCompleted: completed !== withWindow,
        };
    }
    return { status: 'SUCCEEDED', selectedCapability: key, validatedParams: parsed.data };
}
//# sourceMappingURL=copilot.js.map